import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { db } from "../firebase";
import "../css/userdetails.css";

const PAGE_SIZE = 10;

function UserDetails() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  // 📦 PAGE CACHE
  const [warrantyPages, setWarrantyPages] = useState({});
  const [currentWarrantyPage, setCurrentWarrantyPage] = useState(1);

  // 🔖 CURSORS
  const [lastWarrantyDocs, setLastWarrantyDocs] = useState({});

  // ✅ HAS-MORE PER PAGE
  const [hasMoreWarrantyPages, setHasMoreWarrantyPages] = useState({});

  // UPDATE ALL DATA WITH PAGES AFTER DELETING RECORDS
  const [needsRefetch, setNeedsRefetch] = useState(false);

  // 🔍 SEARCH
  const [searchText, setSearchText] = useState("");
  const [searchField, setSearchField] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // 🔍 SEARCH RESULTS (GLOBAL)
  const [searchResults, setSearchResults] = useState([]);

  const [companyName, setCompanyName] = useState("Company");

  /* ===========================
     HELPERS (MUST BE FIRST)
     =========================== */

  const getWarrantyStatus = (row) => {
    let expiryDate = null;

    if (row.warrantyEndDate?.toDate) {
      expiryDate = row.warrantyEndDate.toDate();
    } else if (row.createdAt?.toDate) {
      expiryDate = row.createdAt.toDate();
      expiryDate.setFullYear(expiryDate.getFullYear() + 7);
    }

    if (!expiryDate) return "expired";
    return expiryDate >= new Date() ? "active" : "expired";
  };

  const getWarrantyEndDate = (row) => {
    if (row.warrantyEndDate?.toDate) {
      return row.warrantyEndDate.toDate().toLocaleDateString();
    }

    if (row.createdAt?.toDate) {
      const d = row.createdAt.toDate();
      d.setFullYear(d.getFullYear() + 7);
      return d.toLocaleDateString();
    }

    return "N/A";
  };

  // 🔍 SEARCH ACTIVE FLAG (AFTER HELPERS)
  const isSearchActive =
    searchField && (searchField === "status" ? statusFilter : searchText);

  /* ===========================
     AUTH + INITIAL LOAD
     =========================== */
  useEffect(() => {
    const init = async () => {
      try {
        const storedAuth = sessionStorage.getItem("adminAuth");
        if (!storedAuth) return navigate("/admin");

        const decodedPassword = atob(storedAuth);
        const settingsRef = doc(db, "app-settings", "app-settings");
        const settingsSnap = await getDoc(settingsRef);

        if (
          !settingsSnap.exists() ||
          settingsSnap.data().password !== decodedPassword
        ) {
          sessionStorage.removeItem("adminAuth");
          return navigate("/admin");
        }

        setCompanyName(settingsSnap.data()["company-name"] || "Company");
        await fetchWarrantyPage(1);
        setLoading(false);
      } catch (err) {
        console.error(err);
        navigate("/admin");
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  /* ===========================
     FIRESTORE PAGINATION
     =========================== */
  const fetchWarrantyPage = useCallback(async (page) => {
    if (warrantyPages[page]) {
      setCurrentWarrantyPage(page);
      return;
    }

    const lastDoc = lastWarrantyDocs[page - 1] || null;

    const q = query(
      collection(db, "warranty-card-details"),
      orderBy("createdAt", "desc"),
      ...(lastDoc ? [startAfter(lastDoc)] : []),
      limit(PAGE_SIZE + 1),
    );

    const snap = await getDocs(q);
    let docs = snap.docs;

    const hasMore = docs.length === PAGE_SIZE + 1;
    if (hasMore) docs.pop();

    setWarrantyPages((prev) => ({
      ...prev,
      [page]: docs.map((d) => ({ id: d.id, ...d.data() })),
    }));

    setLastWarrantyDocs((prev) => ({
      ...prev,
      [page]: docs[docs.length - 1] || null,
    }));

    setHasMoreWarrantyPages((prev) => ({
      ...prev,
      [page]: hasMore,
    }));

    setCurrentWarrantyPage(page);
  }, [warrantyPages, lastWarrantyDocs]);

  useEffect(() => {
    if (!needsRefetch) return;

    const refetch = async () => {
      await fetchWarrantyPage(1);
      setNeedsRefetch(false);
    };

    refetch();
  }, [needsRefetch, fetchWarrantyPage]);

  /* ===========================
     GLOBAL SEARCH (ALL PAGES)
     =========================== */
  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!isSearchActive) {
        setSearchResults([]);
        return;
      }

      try {
        const snap = await getDocs(
          query(
            collection(db, "warranty-card-details"),
            orderBy("createdAt", "desc"),
          ),
        );

        let all = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        if (searchField === "status") {
          all = all.filter((row) => getWarrantyStatus(row) === statusFilter);
        } else {
          all = all.filter((row) => {
            const value = String(row[searchField] || "");

            // Mobile number → STARTS WITH
            if (searchField === "mobileNumber") {
              return value.startsWith(searchText);
            }

            // Other fields → normal contains search
            return value.toLowerCase().includes(searchText.toLowerCase());
          });
        }

        setSearchResults(all);
        setCurrentWarrantyPage(1);
      } catch (err) {
        console.error("Search error:", err);
      }
    };

    fetchSearchResults();
  }, [searchText, statusFilter, searchField, isSearchActive]);

  /* ===========================
     DATA SOURCE + PAGINATION
     =========================== */
  const displayedData = useMemo(() => {
    const sourceData = isSearchActive
      ? searchResults
      : warrantyPages[currentWarrantyPage] || [];

    if (!isSearchActive) return sourceData;

    const start = (currentWarrantyPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return sourceData.slice(start, end);
  }, [searchResults, warrantyPages, currentWarrantyPage, isSearchActive]);

  /* ===========================
     DELETE
     =========================== */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;

    try {
      await deleteDoc(doc(db, "warranty-card-details", id));

      // 🔁 Reset pagination cache completely
      setWarrantyPages({});
      setLastWarrantyDocs({});
      setHasMoreWarrantyPages({});
      setCurrentWarrantyPage(1);

      // 🔄 Trigger controlled refetch
      setNeedsRefetch(true);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  if (loading) {
    return <div className="user-details-loader">Loading...</div>;
  }

  return (
    <div className="user-details-container">
      <div className="user-details-card">
        <div className="user-details-header">
          <h2>{companyName} - Warranty Records</h2>
          <button
            className="settings-btn"
            onClick={() => navigate("/admin/settings")}
          >
            Settings
          </button>
        </div>

        {/* SEARCH */}
        <div className="search-bar">
          <select
            value={searchField}
            onChange={(e) => {
              setSearchField(e.target.value);
              setSearchText("");
              setStatusFilter("");
            }}
          >
            <option value="">Search By</option>
            <option value="consumerName">Consumer Name</option>
            <option value="mobileNumber">Mobile Number</option>
            <option value="address">Customer Address</option>
            <option value="dealerName">Dealer Name</option>
            <option value="status">Status</option>
          </select>

          {searchField === "status" ? (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Select Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>
          ) : (
            <input
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              disabled={!searchField}
            />
          )}
        </div>

        {/* TABLE */}
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Mobile</th>
                <th>Customer Address</th>
                <th>Dealer</th>
                <th>Reg. Date</th>
                <th>Expiry Date</th>
                <th>Status</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {displayedData.length ? (
                displayedData.map((row) => (
                  <tr key={row.id}>
                    <td>{row.consumerName}</td>
                    <td>{row.mobileNumber}</td>
                    <td className="address-cell">
                      {row.address || "-"}
                    </td>
                    <td>{row.dealerName || "--"}</td>
                    <td>{row.createdAt?.toDate().toLocaleDateString()}</td>
                    <td>{getWarrantyEndDate(row)}</td>
                    <td>
                      <span
                        className={`warranty-status ${getWarrantyStatus(row)}`}
                      >
                        {getWarrantyStatus(row)}
                      </span>
                    </td>
                    <td>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(row.id)}
                        aria-label="Delete"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="no-data">
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="pagination">
          <button
            disabled={currentWarrantyPage === 1}
            onClick={() => setCurrentWarrantyPage((p) => Math.max(p - 1, 1))}
          >
            Previous
          </button>

          <span className="page-number">Page {currentWarrantyPage}</span>

          {isSearchActive
            ? currentWarrantyPage * PAGE_SIZE < searchResults.length && (
              <button onClick={() => setCurrentWarrantyPage((p) => p + 1)}>
                Next
              </button>
            )
            : hasMoreWarrantyPages[currentWarrantyPage] && (
              <button
                onClick={() => fetchWarrantyPage(currentWarrantyPage + 1)}
              >
                Next
              </button>
            )}
        </div>
      </div>
    </div>
  );
}

export default UserDetails;
